import { useEffect, useState } from "react";
import { format } from "date-fns";
import {
  Button,
  Card,
  Flex,
  FullscreenTakeover,
  LoadingIndicator,
  Text,
  type FileObjectType,
  type Step,
} from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import { withClientContext } from "~/apollo/client";
import { DdqProdOpsTaskKind, DdqProdOpsTaskState } from "~/graphql/supergraphEnums";
import {
  useStartDdqProdOpsTaskMutation,
  useUploadOriginalFormatFileMutation,
} from "~/graphql/supergraphHooks";
import useUploadFilesToS3 from "~/hooks/uploadFilesToS3/useUploadFilesToS3";
import { SPACING } from "~/styles/variables";
import { EmptyState } from "../States/EmptyState";
import { ErrorState } from "../States/ErrorState";
import { StepNavigation } from "../StepNavigation";
import { ApproveForGpModal } from "../steps/GenerateFirstDraftStep/ApproveForGpModal";
import { MarkReadyForReviewModal } from "../steps/GenerateFirstDraftStep/MarkReadyForReviewModal";
import { ReturnForEditsModal } from "../steps/GenerateFirstDraftStep/ReturnForEditsModal";
import { useCheckerHandoff } from "../steps/GenerateFirstDraftStep/useCheckerHandoff";
import * as wizardStyles from "../styles_vanilla.css";
import { CompletedDdqFileCard } from "./CompletedDdqFileCard";
import * as styles from "./styles_vanilla.css";
import type { CompletedDdqFile } from "./CompletedDdqFileCard";
import type { UseDdqProdOpsQueueRowResult } from "../../DDQueue/useDdqProdOpsQueueRow";

export type OriginalFormatWizardProps = UseDdqProdOpsQueueRowResult & {
  handleExit: () => void;
};

type CheckerHandoffModal = "assignReviewer" | "requestEdits" | "approve" | null;

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes}B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(1)}${units[unitIndex]}`;
};

// Original Format has no PDF conversion / structuring / AI drafting sub-wizard — per DDQ-322 it's
// a single upload screen handed off once from the maker to the checker — so it gets its own step
// bar and body here rather than plugging into StandardDdqWizard's step machinery in ../index.tsx.
//
// The completed-DDQ bundle download (zip) and a read-only response viewer for "View completed DDQ"
// don't exist anywhere in the stack (backend decision to skip the asset-bundle work entirely, and
// no frontend route for it either) — those two stay inert. Everything else — the upload mutation,
// the maker/checker hand-off — now calls the real DDQ-322 backend.
export const OriginalFormatWizard = ({
  row,
  isLoading,
  error,
  refetch,
  handleExit,
}: OriginalFormatWizardProps) => {
  const processTask = row?.tasks.find((task) => task.kind === DdqProdOpsTaskKind.Process);
  const reviewTask = row?.tasks.find((task) => task.kind === DdqProdOpsTaskKind.Review);
  // Same rule StandardDdqWizard uses: Process reaching Done is what hands this off to the checker.
  const isChecker =
    processTask?.state === DdqProdOpsTaskState.Done &&
    reviewTask?.state !== DdqProdOpsTaskState.Done;

  // markDdqReadyForReview only assigns the review task, never starts it — landing here as the
  // checker is what starts it (same rule StandardDdqWizard applies).
  const [startReviewTask] = useStartDdqProdOpsTaskMutation({
    context: withClientContext("federation"),
    refetchQueries: ["GetDdqProdOpsQueue"],
  });
  useEffect(() => {
    if (isChecker && reviewTask?.state === DdqProdOpsTaskState.Pending) {
      startReviewTask({ variables: { taskId: reviewTask.id } }).catch(() => {
        // Surfaces naturally: a checker action against a still-pending task fails with its own
        // server error, same as StandardDdqWizard.
      });
    }
  }, [isChecker, reviewTask?.state, reviewTask?.id, startReviewTask]);

  const { uploadFilesToS3, loading: isUploadingToS3 } = useUploadFilesToS3();
  const [uploadOriginalFormatFile, { loading: isSavingUpload }] =
    useUploadOriginalFormatFileMutation({
      context: withClientContext("federation"),
      refetchQueries: ["GetDdqProdOpsQueue"],
    });
  const [uploadError, setUploadError] = useState<string | null>(null);
  // Known for real only within this browser session (the upload that was just picked) — the
  // backend only persists completedResponseDocumentVersionId, not a name, date, or uploader (see
  // DDQ-322), so a checker landing fresh on an already-uploaded task gets this generic fallback.
  const [locallyUploadedFile, setLocallyUploadedFile] = useState<CompletedDdqFile | null>(null);
  const serverKnownFile: CompletedDdqFile | null =
    row?.completedResponseDocumentVersionId != null
      ? { name: "Completed DDQ file", metaLabel: "Uploaded" }
      : null;
  const file = locallyUploadedFile ?? serverKnownFile;

  // The maker uploads while Process is in_progress; the checker replaces while Review is
  // in_progress — the same task id doubles as the mutation's target either way (see DDQ-322).
  const uploadTaskId = isChecker ? reviewTask?.id : processTask?.id;

  const handleFilesSelected = async (accepted: FileObjectType[]) => {
    const [selected] = accepted;
    if (!selected || !uploadTaskId) return;
    setUploadError(null);
    try {
      const [uploaded] = await uploadFilesToS3([selected.file]);
      if (!uploaded) return;
      await uploadOriginalFormatFile({
        variables: {
          input: {
            taskId: uploadTaskId,
            s3ObjectKey: uploaded.objectKey,
            fileName: selected.file.name,
            contentType: selected.file.type,
            size: selected.file.size,
          },
        },
      });
      setLocallyUploadedFile({
        name: selected.file.name,
        metaLabel: `${format(new Date(), "MMM d, yyyy")} · ${formatFileSize(selected.file.size)}`,
      });
    } catch (uploadFailure) {
      setUploadError(
        uploadFailure instanceof Error ? uploadFailure.message : "Something went wrong.",
      );
    }
  };

  const [checkerHandoffModal, setCheckerHandoffModal] = useState<CheckerHandoffModal>(null);
  const closeCheckerHandoffModal = () => setCheckerHandoffModal(null);

  const {
    onMarkReadyForReview,
    isMarkingReady,
    markReadyError,
    onApproveForGp,
    isApproving,
    approveError,
    onReturnForEdits,
    isReturning,
    returnError,
  } = useCheckerHandoff({
    processTaskId: processTask?.id,
    reviewTaskId: reviewTask?.id,
    onMarkedReady: () => {
      closeCheckerHandoffModal();
      handleExit();
    },
    onApproved: () => {
      closeCheckerHandoffModal();
      handleExit();
    },
    onReturned: () => {
      closeCheckerHandoffModal();
      handleExit();
    },
  });

  const steps: Step[] = [
    {
      name: "Complete and upload",
      url: "complete-and-upload",
      status: isChecker ? "complete" : "active",
      hidden: false,
      index: 0,
    },
    {
      name: "Review and submit",
      url: "review-and-submit",
      status: isChecker ? "active" : "inactive",
      hidden: false,
      index: 1,
    },
  ];

  const renderBody = () => {
    if (isLoading) {
      return <LoadingIndicator size="large" data-testid="ddq-wizard-loading-indicator" />;
    }
    if (error && !row) {
      return <ErrorState onExit={handleExit} onRetry={refetch} errorMessage={error.message} />;
    }
    if (!row || !processTask) {
      return <EmptyState onExit={handleExit} />;
    }
    return (
      <Flex flexDirection="column" gap={SPACING.LARGE} className={styles.container}>
        <Card title="Instructions" isFullWidth data-testid="original-format-instructions-card">
          <Flex flexDirection="column" gap={SPACING.SMALL}>
            <Text variant="regular-md">
              {row.requestedBy?.fullName ?? row.lpName ?? "The GP"} requested the completed DDQ
              {row.requestedFormat
                ? ` in ${row.requestedFormat} format`
                : " in its original file format"}
              .
            </Text>
            <ol className={styles.instructionsList}>
              <li>Download the completed PDF and original file</li>
              <li>Use the completed PDF to fill out the original file</li>
              <li>Upload the completed DDQ in its original file format</li>
            </ol>
          </Flex>
        </Card>
        <Card
          title="Download PDF and original file"
          isFullWidth
          data-testid="original-format-download-card"
        >
          <Flex flexDirection="column" gap={SPACING.SMALL}>
            <Flex justifyContent="space-between" alignItems="center" className={styles.fileRow}>
              <Flex flexDirection="column">
                <Text variant="regular-md">{row.title}.zip</Text>
                <Text variant="regular-sm" color={Colors.Neutral600}>
                  Includes original format, completed PDF, and any attachments
                </Text>
              </Flex>
              <Button variant="neutral" disabled data-testid="original-format-download-zip">
                Download .zip
              </Button>
            </Flex>
            <Button
              variant="link"
              displayAsLink
              isInline
              disabled
              data-testid="original-format-view-completed"
            >
              View completed DDQ
            </Button>
          </Flex>
        </Card>
        <CompletedDdqFileCard
          title={isChecker ? "Review completed DDQ" : "Upload the completed DDQ"}
          description="Upload the completed file. Make sure to include all attachments as a .zip file."
          file={file}
          isUploading={isUploadingToS3 || isSavingUpload}
          uploadError={uploadError}
          onFilesSelected={(accepted) => void handleFilesSelected(accepted)}
        />
      </Flex>
    );
  };

  const footerContent = isChecker ? (
    <Flex justifyContent="flex-end" gap={SPACING.SMALL} className={wizardStyles.footerBar}>
      <Button
        variant="neutral"
        onClick={() => setCheckerHandoffModal("requestEdits")}
        data-testid="original-format-request-edits-trigger"
      >
        Request edits
      </Button>
      <Button
        onClick={() => setCheckerHandoffModal("approve")}
        data-testid="original-format-approve-trigger"
      >
        Approve for GP
      </Button>
    </Flex>
  ) : processTask ? (
    <Flex justifyContent="flex-end" className={wizardStyles.footerBar}>
      <Button
        disabled={!file}
        onClick={() => setCheckerHandoffModal("assignReviewer")}
        data-testid="original-format-assign-reviewer-trigger"
      >
        Assign reviewer
      </Button>
    </Flex>
  ) : null;

  return (
    <>
      <FullscreenTakeover>
        <FullscreenTakeover.Header
          onExit={handleExit}
          title={row?.title ?? "Process DDQ"}
          subtitle={row?.lpName ?? undefined}
        />
        <FullscreenTakeover.Subheader>
          <StepNavigation steps={steps} />
        </FullscreenTakeover.Subheader>
        <FullscreenTakeover.Body>{renderBody()}</FullscreenTakeover.Body>
        {footerContent && (
          <FullscreenTakeover.Footer>{footerContent}</FullscreenTakeover.Footer>
        )}
      </FullscreenTakeover>
      {checkerHandoffModal === "assignReviewer" && (
        <MarkReadyForReviewModal
          errorMessage={markReadyError}
          isSubmitting={isMarkingReady}
          onHide={closeCheckerHandoffModal}
          onConfirm={onMarkReadyForReview}
        />
      )}
      {checkerHandoffModal === "requestEdits" && (
        <ReturnForEditsModal
          variant="originalFormat"
          makerName={processTask?.assignee?.fullName ?? undefined}
          errorMessage={returnError}
          isSubmitting={isReturning}
          onHide={closeCheckerHandoffModal}
          onConfirm={onReturnForEdits}
        />
      )}
      {checkerHandoffModal === "approve" && (
        <ApproveForGpModal
          variant="originalFormat"
          owners={[]}
          errorMessage={approveError}
          isSubmitting={isApproving}
          onHide={closeCheckerHandoffModal}
          onConfirm={onApproveForGp}
        />
      )}
    </>
  );
};
